import { useEffect, useState } from "react";
import { Save, Image as ImageIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import UploadButton from "./UploadButton";

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {textarea
        ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
        : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />}
    </div>
  );
}

function ImgField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-sm flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {label}</Label>
      <div className="flex gap-3 mt-1">
        {value ? <img src={value} alt="" className="w-16 h-16 rounded-lg object-cover border border-stone-200" /> : <div className="w-16 h-16 rounded-lg bg-stone-100 border" />}
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL immagine (https://...)" className="flex-1" data-testid={testid} />
        <UploadButton onUploaded={onChange} testid={testid ? `${testid}-upload` : undefined} />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6">
      <h3 className="font-serif text-lg font-semibold text-[#0B2A45] mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function ContentPanel() {
  const [c, setC] = useState(null);

  useEffect(() => { api.get("/content").then(({ data }) => setC(data)).catch(() => {}); }, []);

  if (!c) return <div className="text-stone-400 py-8">Caricamento contenuti…</div>;

  const set = (section, key, val) => setC({ ...c, [section]: { ...c[section], [key]: val } });
  const setPillar = (i, key, val) => {
    const pillars = [...(c.philosophy.pillars || [])];
    pillars[i] = { ...pillars[i], [key]: val };
    setC({ ...c, philosophy: { ...c.philosophy, pillars } });
  };
  const setArticle = (i, key, val, lock = false) => {
    const arts = [...(c.guide?.articles || [])]; arts[i] = { ...arts[i], [key]: val };
    if (lock) arts[i].slugLocked = true;
    if (key === "title" && !arts[i].slugLocked) arts[i].slug = slugify(val);
    setC({ ...c, guide: { ...c.guide, articles: arts } });
  };
  const addArticle = () => setC({ ...c, guide: { ...c.guide, articles: [...(c.guide?.articles || []), { slug: "", title: "", excerpt: "", body: "" }] } });
  const removeArticle = (i) => { if (window.confirm("Eliminare questo articolo? La pagina /guida/... non sarà più raggiungibile.")) setC({ ...c, guide: { ...c.guide, articles: (c.guide?.articles || []).filter((_, j) => j !== i) } }); };
  const setBullet = (i, val) => {
    const bullets = [...(c.comodato.bullets || [])];
    bullets[i] = val;
    setC({ ...c, comodato: { ...c.comodato, bullets } });
  };
  const reviews = c.reviews || { eyebrow: "Dicono di noi", title: "Chi ci ha scelto", items: [] };
  const setReview = (i, key, val) => {
    const items = [...(reviews.items || [])];
    items[i] = { ...items[i], [key]: val };
    setC({ ...c, reviews: { ...reviews, items } });
  };
  const addReview = () => setC({ ...c, reviews: { ...reviews, items: [...(reviews.items || []), { name: "", role: "", rating: 5, text: "" }] } });
  const removeReview = (i) => setC({ ...c, reviews: { ...reviews, items: (reviews.items || []).filter((_, j) => j !== i) } });

  const save = async () => {
    const articles = (c.guide?.articles || []).filter((a) => (a.title || "").trim() && (a.slug || "").trim());
    const payload = { ...c, guide: { ...c.guide, articles } };
    try { await api.put("/content", { content: payload }); setC(payload); toast.success("Contenuti del sito aggiornati"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">Modifica foto e testi della home pubblica. Le modifiche sono immediate sul sito.</p>
        <Button onClick={save} data-testid="save-content-btn" className="bg-[#0F4C81] text-white rounded-full"><Save className="w-4 h-4 mr-2" />Salva contenuti</Button>
      </div>

      <Section title="Hero (testata)">
        <ImgField label="Foto principale" value={c.hero.image} onChange={(v) => set("hero", "image", v)} testid="content-hero-image" />
        <Field label="Etichetta piccola" value={c.hero.badge} onChange={(v) => set("hero", "badge", v)} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Titolo (1)" value={c.hero.title} onChange={(v) => set("hero", "title", v)} testid="content-hero-title" />
          <Field label="Parola evidenziata" value={c.hero.title_accent} onChange={(v) => set("hero", "title_accent", v)} />
          <Field label="Titolo (fine)" value={c.hero.title_suffix} onChange={(v) => set("hero", "title_suffix", v)} />
        </div>
        <Field label="Sottotitolo" value={c.hero.subtitle} onChange={(v) => set("hero", "subtitle", v)} textarea />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Stat 1 valore" value={c.hero.stat1_value} onChange={(v) => set("hero", "stat1_value", v)} />
          <Field label="Stat 2 valore" value={c.hero.stat2_value} onChange={(v) => set("hero", "stat2_value", v)} />
          <Field label="Stat 3 valore" value={c.hero.stat3_value} onChange={(v) => set("hero", "stat3_value", v)} />
          <Field label="Stat 1 etichetta" value={c.hero.stat1_label} onChange={(v) => set("hero", "stat1_label", v)} />
          <Field label="Stat 2 etichetta" value={c.hero.stat2_label} onChange={(v) => set("hero", "stat2_label", v)} />
          <Field label="Stat 3 etichetta" value={c.hero.stat3_label} onChange={(v) => set("hero", "stat3_label", v)} />
        </div>
      </Section>

      <Section title="Filosofia (3 valori)">
        <Field label="Eyebrow" value={c.philosophy.eyebrow} onChange={(v) => set("philosophy", "eyebrow", v)} />
        <Field label="Titolo" value={c.philosophy.title} onChange={(v) => set("philosophy", "title", v)} />
        {(c.philosophy.pillars || []).map((p, i) => (
          <div key={`pillar-${i}`} className="border-t border-stone-100 pt-3">
            <Field label={`Valore ${i + 1} — titolo`} value={p.title} onChange={(v) => setPillar(i, "title", v)} />
            <div className="mt-2"><Field label={`Valore ${i + 1} — testo`} value={p.text} onChange={(v) => setPillar(i, "text", v)} textarea /></div>
          </div>
        ))}
      </Section>

      <Section title="Sezione Prodotti">
        <Field label="Eyebrow" value={c.products.eyebrow} onChange={(v) => set("products", "eyebrow", v)} />
        <Field label="Titolo" value={c.products.title} onChange={(v) => set("products", "title", v)} />
        <Field label="Nota" value={c.products.note} onChange={(v) => set("products", "note", v)} textarea />
      </Section>

      <Section title="Sezione Comodato">
        <Field label="Eyebrow" value={c.comodato.eyebrow} onChange={(v) => set("comodato", "eyebrow", v)} />
        <Field label="Titolo" value={c.comodato.title} onChange={(v) => set("comodato", "title", v)} />
        <Field label="Testo" value={c.comodato.text} onChange={(v) => set("comodato", "text", v)} textarea />
        {(c.comodato.bullets || []).map((b, i) => (
          <Field key={`bullet-${i}`} label={`Punto elenco ${i + 1}`} value={b} onChange={(v) => setBullet(i, v)} />
        ))}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ImgField label="Foto 1" value={c.comodato.image1} onChange={(v) => set("comodato", "image1", v)} />
          <ImgField label="Foto 2" value={c.comodato.image2} onChange={(v) => set("comodato", "image2", v)} />
        </div>
      </Section>

      <Section title="Sezione Territorio">
        <Field label="Eyebrow" value={c.territory.eyebrow} onChange={(v) => set("territory", "eyebrow", v)} />
        <Field label="Titolo" value={c.territory.title} onChange={(v) => set("territory", "title", v)} />
        <Field label="Testo" value={c.territory.text} onChange={(v) => set("territory", "text", v)} textarea />
        <ImgField label="Foto sfondo" value={c.territory.image} onChange={(v) => set("territory", "image", v)} testid="content-territory-image" />
      </Section>

      <Section title="Guida SEO (articoli)">
        <Field label="Titolo pagina" value={c.guide?.title} onChange={(v) => set("guide", "title", v)} />
        <Field label="Introduzione" value={c.guide?.intro} onChange={(v) => set("guide", "intro", v)} textarea />
        {(c.guide?.articles || []).map((a, i) => (
          <div key={`guide-${i}`} className="border-t border-stone-100 pt-3 space-y-2" data-testid={`guide-editor-${i}`}>
            <Field label={`Articolo ${i + 1} — titolo`} value={a.title} onChange={(v) => setArticle(i, "title", v)} testid={`guide-title-${i}`} />
            <Field label="Slug URL (generato dal titolo, modificabile)" value={a.slug} onChange={(v) => setArticle(i, "slug", slugify(v), true)} testid={`guide-slug-${i}`} />
            <Field label="Sottotitolo / estratto" value={a.excerpt} onChange={(v) => setArticle(i, "excerpt", v)} textarea testid={`guide-excerpt-${i}`} />
            <Field label="Testo" value={a.body} onChange={(v) => setArticle(i, "body", v)} textarea testid={`guide-body-${i}`} />
            <div className="flex items-center gap-4">
              {a.slug && <a href={`/guida/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#0F4C81] underline">Anteprima /guida/{a.slug}</a>}
              <button type="button" onClick={() => removeArticle(i)} className="text-xs text-red-600 underline" data-testid={`guide-remove-${i}`}>Elimina articolo</button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addArticle} className="rounded-full border-stone-300" data-testid="guide-add-btn">+ Nuovo articolo</Button>
        <p className="text-xs text-stone-400">Dopo il salvataggio l'articolo compare in Home, nella pagina Guida e nella sitemap per Google.</p>
      </Section>

      <Section title="Recensioni clienti">
        <Field label="Eyebrow" value={c.reviews?.eyebrow} onChange={(v) => set("reviews", "eyebrow", v)} />
        <Field label="Titolo" value={c.reviews?.title} onChange={(v) => set("reviews", "title", v)} />
        {(c.reviews?.items || []).map((r, i) => (
          <div key={`review-${i}`} className="border-t border-stone-100 pt-3 space-y-2" data-testid={`review-editor-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label={`Recensione ${i + 1} — nome`} value={r.name} onChange={(v) => setReview(i, "name", v)} testid={`review-name-${i}`} />
              <Field label="Locale / città" value={r.role} onChange={(v) => setReview(i, "role", v)} />
              <div><Label className="text-sm">Stelle (1-5)</Label><Input type="number" min={1} max={5} value={r.rating ?? 5} onChange={(e) => setReview(i, "rating", Math.max(1, Math.min(5, Number(e.target.value) || 5)))} className="mt-1" /></div>
            </div>
            <Field label="Testo" value={r.text} onChange={(v) => setReview(i, "text", v)} textarea />
            <button type="button" onClick={() => removeReview(i)} className="text-xs text-red-600 underline" data-testid={`review-remove-${i}`}>Rimuovi recensione</button>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addReview} className="rounded-full border-stone-300" data-testid="review-add-btn">+ Aggiungi recensione</Button>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} className="bg-[#0F4C81] text-white rounded-full"><Save className="w-4 h-4 mr-2" />Salva contenuti</Button>
      </div>
    </div>
  );
}
